"use client"
import image1 from '../assets/image_1.jpg';
import image2 from '../assets/image_2.jpg';
import image3 from '../assets/image_3.jpg';
import { useState, useEffect, useContext } from "react"
import { useNavigate } from "react-router-dom"
import { LoginContext } from "../LoginContext"
import { useTranslation } from "react-i18next"
import {
  X,
  ChevronRight,
  ChevronLeft,
  DollarSign,
  Smartphone,
  Repeat,
  Cpu,
  Globe,
  FileText,
  Printer,
  Clock,
  Leaf,
  ArrowRight,
  PlayCircle,
} from "lucide-react";
import FloatingElements3D from "../components/FloatingElements3D";
import RegistrationForm from "../components/RegistrationForm";

const APP_MODE = process.env.REACT_APP_APP_MODE;
const LAUNCH_DATE_STR = process.env.REACT_APP_LAUNCH_DATE;
const LAUNCH_DATE = LAUNCH_DATE_STR ? new Date(LAUNCH_DATE_STR).getTime() : null;



const useCountdown = () => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (APP_MODE !== "live" || !LAUNCH_DATE || now >= LAUNCH_DATE) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [now]);

  if (APP_MODE !== "live" || !LAUNCH_DATE || now >= LAUNCH_DATE) return null;

  const diff = LAUNCH_DATE - now;
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000),
  };
};

const PilotBanner = ({ t, navigate, dismissBanner, onRegister }) => {
  const countdown = useCountdown();
  const isLive = APP_MODE === "live" && countdown;

  // Live mode: large hero-style banner with countdown
  if (isLive) {
    const timeBlocks = [
      { value: countdown.days, label: t("countdown.days", "Tage") },
      { value: countdown.hours, label: t("countdown.hours", "Stunden") },
      { value: countdown.minutes, label: t("countdown.minutes", "Minuten") },
      { value: countdown.seconds, label: t("countdown.seconds", "Sekunden") },
    ];

    return (
      <div className="relative z-20 bg-gradient-to-br from-[#0a5a75] via-[#0f7699] to-[#4c9fb9] text-white overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-white rounded-full blur-3xl" />
        </div>

        {/* Dismiss button */}
        <button
          onClick={dismissBanner}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"
          aria-label="Close banner"
        >
          <X size={18} />
        </button>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 flex flex-col items-center text-center">
          {/* Badge */}
          <div className="inline-block px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-xs sm:text-sm font-semibold tracking-widest uppercase mb-4 sm:mb-5 border border-white/20">
            EARLY ACCESS
          </div>

          {/* Title */}
          <h2 className="text-xl sm:text-3xl md:text-4xl font-extrabold mb-2 sm:mb-3 leading-tight">
            {t("pilotBanner.text")}
          </h2>

          {/* Subtitle */}
          <p className="text-white/80 text-sm sm:text-base mb-6 sm:mb-8 max-w-lg">
            {t("pilotBanner.subtitle", "Sichern Sie sich jetzt Ihren kostenlosen Zugang zur Pilotphase.")}
          </p>

          {/* Countdown */}
          <div className="flex gap-2 sm:gap-4 mb-6 sm:mb-8">
            {timeBlocks.map(({ value, label }, idx) => (
              <div key={label} className="flex items-center gap-2 sm:gap-4">
                <div className="flex flex-col items-center">
                  <div className="bg-white/15 backdrop-blur-sm rounded-xl sm:rounded-2xl px-3 sm:px-5 py-2 sm:py-3 min-w-[52px] sm:min-w-[80px] border border-white/20">
                    <span className="text-2xl sm:text-4xl md:text-5xl font-mono font-bold tabular-nums">
                      {String(value).padStart(2, "0")}
                    </span>
                  </div>
                  <span className="text-[10px] sm:text-xs mt-1.5 text-white/60 uppercase tracking-wider">
                    {label}
                  </span>
                </div>
                {idx < 3 && (
                  <span className="text-xl sm:text-3xl font-bold text-white/40 -mt-4 sm:-mt-5">:</span>
                )}
              </div>
            ))}
          </div>

          {/* Launch date */}
          <p className="text-white/50 text-xs sm:text-sm mb-5">
            {t("countdown.launchDate", "Start")}: {new Date(LAUNCH_DATE).toLocaleDateString()}
          </p>

          {/* CTA */}
          <button
            onClick={onRegister}
            className="bg-white text-[#0f7699] font-bold text-sm sm:text-base px-8 sm:px-10 py-3 sm:py-3.5 rounded-2xl hover:bg-slate-100 transition-all hover:scale-105 active:scale-95 shadow-xl shadow-black/10"
          >
            {t("pilotBanner.cta")}
          </button>
        </div>
      </div>
    );
  }

  // Demo mode: compact banner
  return (
    <div className="relative z-20 bg-gradient-to-r from-[#0f7699] to-[#4c9fb9] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
        <p className="text-sm sm:text-base font-bold flex-1 text-center sm:text-left">
          {t("pilotBanner.text")}
        </p>
        <button
          onClick={onRegister}
          className="shrink-0 bg-white text-[#0f7699] font-black text-xs sm:text-sm px-4 sm:px-6 py-2 rounded-xl hover:bg-slate-100 transition-all hover:scale-105 active:scale-95 shadow-lg"
        >
          {t("pilotBanner.cta")}
        </button>
        <button
          onClick={dismissBanner}
          className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"
          aria-label="Close banner"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
};

const PageLoading = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [currentSlide, setCurrentSlide] = useState(0)
  const [showVideo, setShowVideo] = useState(false)
  const [showBanner, setShowBanner] = useState(() => {
    return localStorage.getItem("pilotBannerDismissed") !== "true"
  })
  const [isRegisterOpen, setIsRegisterOpen] = useState(false)

  const dismissBanner = () => {
    setShowBanner(false)
    localStorage.setItem("pilotBannerDismissed", "true")
  }


  const features = [
  {
    title: t("menu.features.flexibility.title"),
    description: t("menu.features.flexibility.description"),
    icon: Smartphone,
    color: "#0f7699",
  },
  {
    title: t("menu.features.efficient.title"),
    description: t("menu.features.efficient.description"),
    icon: Repeat,
    color: "#4c9fb9",
  },
  {
    title: t("menu.features.userFriendly.title"),
    description: t("menu.features.userFriendly.description"),
    icon: Cpu,
    color: "#0f7699",
  },
  {
    title: t("menu.features.compatibility.title"),
    description: t("menu.features.compatibility.description"),
    icon: Globe,
    color: "#4c9fb9",
  },
  {
    title: t("menu.features.transparency.title"),
    description: t("menu.features.transparency.description"),
    icon: FileText,
    color: "#0f7699",
  },
  {
    title: t("menu.features.printing.title"),
    description: t("menu.features.printing.description"),
    icon: Printer,
    color: "#4c9fb9",
  },
  {
    title: t("menu.features.timeSaving.title"),
    description: t("menu.features.timeSaving.description"),
    icon: Clock,
    color: "#0f7699",
  },
  {
    title: t("menu.features.qualityCost.title"),
    description: t("menu.features.qualityCost.description"),
    icon: DollarSign,
    color: "#4c9fb9",
  },
  {
    title: t("menu.features.ecoFriendly.title"),
    description: t("menu.features.ecoFriendly.description"),
    icon: Leaf,
    color: "#0f7699",
  },
]


  const slides = [
  {
    image: image1,
    title: t("menu.slideshow.brace.title"),
    description: t("menu.slideshow.brace.description"),
  },
  {
    image: image2,
    title: t("menu.slideshow.casts.title"),
    description: t("menu.slideshow.casts.description"),
  },
  {
    image: image3,
    title: t("menu.slideshow.freedom.title"),
    description: t("menu.slideshow.freedom.description"),
  },
  ]


  const steps = [
  {
    title: t("menu.howItWorks.steps.1.title"),
    description: t("menu.howItWorks.steps.1.description"),
    icon: PlayCircle,
    color: "#0f7699",
  },
  {
    title: t("menu.howItWorks.steps.2.title"),
    description: t("menu.howItWorks.steps.2.description"),
    icon: Smartphone,
    color: "#4c9fb9",
  },
  {
    title: t("menu.howItWorks.steps.3.title"),
    description: t("menu.howItWorks.steps.3.description"),
    icon: Cpu,
    color: "#0f7699",
  },
  {
    title: t("menu.howItWorks.steps.4.title"),
    description: t("menu.howItWorks.steps.4.description"),
    icon: FileText,
    color: "#4c9fb9",
  },
  {
    title: t("menu.howItWorks.steps.5.title"),
    description: t("menu.howItWorks.steps.5.description"),
    icon: Printer,
    color: "#0f7699",
  },
]


  const prevSlide = () => setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1))

  const nextSlide = () => setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1))

  // Auto-advance slides
  useEffect(() => {
    const timer = setInterval(nextSlide, 5000)
    return () => clearInterval(timer)
  }, [])

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 relative overflow-hidden transition-colors duration-300">
      <FloatingElements3D count={8} />

      {/* Pilot Phase Banner */}
      {showBanner && <PilotBanner t={t} navigate={navigate} dismissBanner={dismissBanner} onRegister={() => setIsRegisterOpen(true)} />}

      {/* Registration Modal */}
      {isRegisterOpen && (
        <RegistrationForm onClose={() => setIsRegisterOpen(false)} />
      )}

      {/* Enhanced Slideshow Section */}
      <section className="min-h-[calc(100dvh-5rem)] flex flex-col py-6 sm:py-20 px-4 sm:px-6 bg-white dark:bg-slate-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto w-full flex-1 flex flex-col">
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-center text-[#0f7699] dark:text-[#4fc3f7] mb-4 md:mb-16 relative inline-block left-1/2 -translate-x-1/2 after:content-[''] after:absolute after:-bottom-2 md:after:-bottom-4 after:left-1/2 after:-translate-x-1/2 after:w-16 md:after:w-20 after:h-1 after:bg-[#0f7699] dark:after:bg-[#4fc3f7] after:rounded-full">
            {t("menu.heroSection.ourServices")}
          </h2>

          <div className="flex-1 flex flex-col md:flex-row items-center gap-4 md:gap-20 relative md:min-h-[500px]">
            {/* Enhanced Image Section with Nav Buttons */}
            <div className="flex-1 w-full relative flex items-center gap-2 md:block min-h-0">
              {/* Mobile: Left Nav Button */}
              <button
                onClick={prevSlide}
                className="md:hidden shrink-0 w-10 h-10 bg-white/90 dark:bg-slate-700/90 backdrop-blur-md rounded-xl flex items-center justify-center text-[#0f7699] dark:text-[#4fc3f7] shadow-xl transition-all active:scale-95 z-20"
                aria-label="Previous slide"
              >
                <ChevronLeft size={22} />
              </button>

              <div className="flex-1 md:flex-none h-full md:aspect-square overflow-hidden rounded-2xl md:rounded-[2rem] shadow-2xl transition-all duration-500 relative group">
                <img
                  src={slides[currentSlide].image}
                  alt={slides[currentSlide].title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />

                {/* Slide Indicators */}
                <div className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 flex gap-2.5 z-10">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-2.5 h-2.5 md:w-3 md:h-3 rounded-full transition-all duration-300 ${
                        index === currentSlide ? "bg-white scale-125" : "bg-white/50 hover:bg-white/80"
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Mobile: Right Nav Button */}
              <button
                onClick={nextSlide}
                className="md:hidden shrink-0 w-10 h-10 bg-white/90 dark:bg-slate-700/90 backdrop-blur-md rounded-xl flex items-center justify-center text-[#0f7699] dark:text-[#4fc3f7] shadow-xl transition-all active:scale-95 z-20"
                aria-label="Next slide"
              >
                <ChevronRight size={22} />
              </button>

              {/* Desktop: Nav Buttons (absolute positioned) */}
              <button
                onClick={prevSlide}
                className="hidden md:flex absolute top-1/2 -left-8 -translate-y-1/2 w-14 h-14 bg-white/90 dark:bg-slate-700/90 backdrop-blur-md rounded-2xl items-center justify-center text-[#0f7699] dark:text-[#4fc3f7] shadow-xl transition-all hover:scale-110 active:scale-95 z-20"
                aria-label="Previous slide"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={nextSlide}
                className="hidden md:flex absolute top-1/2 -right-8 -translate-y-1/2 w-14 h-14 bg-white/90 dark:bg-slate-700/90 backdrop-blur-md rounded-2xl items-center justify-center text-[#0f7699] dark:text-[#4fc3f7] shadow-xl transition-all hover:scale-110 active:scale-95 z-20"
                aria-label="Next slide"
              >
                <ChevronRight size={24} />
              </button>
            </div>

            {/* Enhanced Text Section — fixed height on mobile to prevent layout shift */}
            <div className="w-full md:flex-1 shrink-0 h-[11rem] md:h-auto overflow-hidden md:overflow-visible">
              <h3 className="text-lg md:text-4xl font-bold text-[#0f7699] dark:text-[#4fc3f7] mb-2 md:mb-6">
                {slides[currentSlide].title}
              </h3>
              <p className="text-sm md:text-xl text-slate-500 dark:text-slate-300 leading-relaxed mb-4 md:mb-10">
                {slides[currentSlide].description}
              </p>
              <button className="px-5 md:px-8 py-2.5 md:py-3.5 border-2 border-[#0f7699] dark:border-[#4fc3f7] text-[#0f7699] dark:text-[#4fc3f7] rounded-2xl font-bold flex items-center gap-2 transition-all hover:bg-[#0f7699] dark:hover:bg-[#4fc3f7] hover:text-white dark:hover:text-slate-900 group text-sm md:text-base">
                {t("menu.buttons.learnMore")}
                <ArrowRight size={18} className="translate-x-0 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-12 sm:py-24 px-4 sm:px-6 bg-slate-100 dark:bg-slate-900 transition-colors duration-300">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center text-[#0f7699] dark:text-[#4fc3f7] mb-8 md:mb-16 relative inline-block left-1/2 -translate-x-1/2 after:content-[''] after:absolute after:-bottom-4 after:left-1/2 after:-translate-x-1/2 after:w-20 after:h-1 after:bg-[#0f7699] dark:after:bg-[#4fc3f7] after:rounded-full">
            {t("menu.heroSection.howItWorks")}
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-8">
            {steps.map(({ title, description, icon: Icon, color }, index) => (
              <div
                key={index}
                className="card-3d-hover-orbit bg-white dark:bg-slate-800 rounded-[1.5rem] sm:rounded-[2rem] p-5 sm:p-8 flex flex-col items-center text-center shadow-lg transition-all hover:shadow-2xl"
              >
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
                  style={{ backgroundColor: `${color}15`, color }}
                >
                  <Icon size={32} />
                </div>
                <h4 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-4">{title}</h4>
                <p className="text-slate-500 dark:text-slate-400 leading-relaxed text-sm">{description}</p>
              </div>
            ))}
          </div>

          <div className="mt-20 flex flex-col items-center">
            {!showVideo ? (
              <button
                onClick={() => setShowVideo(true)}
                className="px-10 py-4 bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900 rounded-2xl font-bold flex items-center gap-3 transition-all hover:bg-[#0a5f7a] dark:hover:bg-[#81d4fa] shadow-lg shadow-[#0f7699]/20 dark:shadow-[#4fc3f7]/20"
              >
                <PlayCircle size={24} />
                {t("menu.buttons.watchVideo")}
              </button>
            ) : (
              <div className="w-full max-w-4xl aspect-video rounded-3xl overflow-hidden shadow-2xl border-4 border-white/50 dark:border-slate-700/50">
                <iframe
                  src="https://www.youtube.com/embed/PeJW6ZSnOhQ?rel=0&modestbranding=1&controls=1"
                  title="YouTube Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 sm:py-24 px-4 sm:px-6 bg-white dark:bg-slate-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center text-[#0f7699] dark:text-[#4fc3f7] mb-8 md:mb-16 relative inline-block left-1/2 -translate-x-1/2 after:content-[''] after:absolute after:-bottom-4 after:left-1/2 after:-translate-x-1/2 after:w-20 after:h-1 after:bg-[#0f7699] dark:after:bg-[#4fc3f7] after:rounded-full">
            {t("menu.heroSection.features")}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-10">
            {features.map(({ title, description, icon: Icon, color }, index) => (
              <div
                key={index}
                className="bg-slate-50 dark:bg-slate-700 rounded-[2rem] sm:rounded-[2.5rem] p-6 sm:p-8 md:p-10 flex flex-col items-center text-center group hover:bg-white dark:hover:bg-slate-600 border border-transparent hover:border-[#0f7699]/10 dark:hover:border-[#4fc3f7]/20 transition-all shadow-sm hover:shadow-xl"
              >
                <div
                  className="w-16 h-16 rounded-3xl flex items-center justify-center mb-8 rotate-3 group-hover:rotate-0 transition-transform duration-500"
                  style={{ backgroundColor: `${color}15`, color }}
                >
                  <Icon size={32} />
                </div>
                <h4 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-4">{title}</h4>
                <p className="text-slate-500 dark:text-slate-400 leading-relaxed text-sm">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PageLoading;
